/**
 * ZoneController — owns all zone-switch logic.
 *
 * Extracted from MiniPlayer.selectZone to keep MiniPlayer a pure UI component.
 * Handles: teardown of old zone, store update, playback handoff to new zone,
 * iOS gesture-safe path, sync-aware adoption, format negotiation.
 */

import { useMaestroStore } from '../store/maestroStore';
import { TransportCommand } from './TransportCommand';
import { diagnosticLog } from '../utils/diagnosticLogger';
import { connectorRendererFromZone } from './gaplessSetNext';
import { QueueController } from './QueueController';
import { isLimitedRenderer, getFormatIdForRenderer } from './types';
import { getActiveTrack, isRadioActive } from './PlaybackSnapshot';
import { getClientTransport } from '../handlers/ClientTransportHandler';
import { getSyncHandler } from '../handlers/ClientSyncHandler';
import { detectPlatform } from '../utils/platform';
import { isLocalLibraryTrack, resolveLocalFileUrl } from '../lib/playback-bridge';
import { LOCAL_ZONE_STORAGE_ID } from '../utils/localZoneStorage';
import { resolveZoneDisplayName } from '../utils/zoneDisplayName';
import { classifySignalPathZone } from '../utils/signalPath';
import { logSignalPathZoneActivation } from '../utils/signalPathTelemetry';
import { executeMidTrackZoneTransfer } from './zoneTransfer';
import type { ZoneRow } from './zoneRestorePick';
import { persistSavedZoneMeta } from './zoneRestorePick';

type ZoneInput = string | {
  id: string;
  name?: string;
  type?: string;
  manufacturer?: string;
  [key: string]: unknown;
};

function isLocalZoneType(zone: { id: string; type?: string }): boolean {
  return zone.type === 'local' || zone.type === 'browser'
    || zone.id === 'this_computer' || zone.id === 'local_computer' || zone.id === 'this_device';
}

function isIOSNative(): boolean {
  return detectPlatform().isIOSNativeApp;
}

function zoneDisplayLabel(zone: ZoneInput): string {
  if (typeof zone === 'string') return zone;
  const store = useMaestroStore.getState();
  return resolveZoneDisplayName(zone, {
    discoveredZones: store.discovery?.discoveredZones,
    selectedUsbDac: store.selectedUsbDac,
  });
}

class ZoneControllerImpl {
  private _busy = false;

  async selectZone(zone: ZoneInput): Promise<void> {
    if (this._busy) {
      console.log('[ZoneController] SKIPPED — already in progress');
      return;
    }
    void import('../radio/ellaRadioSessionState').then(({ disarmRadioAutoResume }) => {
      disarmRadioAutoResume('renderer_switch');
    });
    this._busy = true;
    try {
      await this._doSelect(zone);
    } catch (err) {
      console.error('[ZoneController] ERROR:', err);
      const target =
        typeof zone === 'string' ? zone : (zoneDisplayLabel(zone) || '?');
      const prev = useMaestroStore.getState().discovery?.activeZone?.name;
      diagnosticLog(
        'error',
        'transport',
        `Zone switch failed: ${target} — ${String((err as any)?.message || err)}`,
        { targetZone: target, reason: String((err as any)?.message || err), previousZone: prev },
      );
    } finally {
      this._busy = false;
    }
  }

  private async _doSelect(zone: ZoneInput): Promise<void> {
    const { getClientViewHandler } = await import('../handlers/ClientViewHandler');
    const viewHandler = getClientViewHandler();
    const store = useMaestroStore.getState();
    const prevAz = store.discovery?.activeZone;
    const oldRenderer = store.activeConnectorRenderer;
    // `wasPlaying` must include radio playback too — otherwise the old
    // renderer never gets torn down when users switch zones while radio
    // is streaming on it (e.g. "Radio on PMG" → "This Computer" used to
    // leave PMG playing the radio stream forever).
    const radioIsPlayingAtEntry = this._isRadioPlaying();
    const wasPlaying = store.playbackState === 'playing' || radioIsPlayingAtEntry;
    const currentTrack = store.currentTrack;

    const newZoneName = zoneDisplayLabel(zone);
    diagnosticLog(
      'info',
      'transport',
      `Zone changed: ${prevAz?.name || 'none'} → ${newZoneName}`,
      {
        from: prevAz?.name,
        to: newZoneName,
        rendererType: (zone as any)?.transport_type || (zone as any)?.type,
      },
    );
    console.log('[ZoneController] ENTER:', typeof zone === 'string' ? zone : (zone as any)?.name, (zone as any)?.id);

    // String-only zone name (legacy)
    if (typeof zone === 'string') {
      store.setZoneName(zone);
      viewHandler.setZone(zone);
      return;
    }

    if (!zone?.id) return;

    const isLocal = isLocalZoneType(zone as { id: string; type?: string });
    const isSame = oldRenderer && zone.id === oldRenderer.id;

    // ── iOS gesture-safe path ──
    // Start local playback SYNCHRONOUSLY within the tap gesture so
    // audio.play() is allowed by iOS. Stop the old renderer AFTER.
    if (isIOSNative() && isLocal && !isSame) {
      this._iosGestureSafePath(zone, store, viewHandler, oldRenderer, wasPlaying, currentTrack);
      return;
    }

    // ── Standard path ──

    // 1. Tear down old zone
    await this._teardownOldZone(isSame, oldRenderer, wasPlaying);

    // 2. Update store to new zone
    this._updateStore(zone, isLocal, store, viewHandler);

    // 3. Check sync — adopt if renderer is already playing via another device
    if (this._trySyncAdopt(zone, isSame, store)) return;

    getSyncHandler().claimControl();

    if (wasPlaying && !isSame) {
      const positionSeconds = store.position;
      await executeMidTrackZoneTransfer({
        zone: zone as ZoneRow,
        isLocal,
        currentTrack: store.currentTrack,
        positionSeconds,
        oldRenderer,
        wasExclusive: TransportCommand.isExclusiveActive(),
        stopAlreadyDone: true,
      });
    }
  }

  // ── iOS gesture-safe: start local immediately, stop old renderer after ──
  private _iosGestureSafePath(
    zone: any, store: any, viewHandler: any,
    oldRenderer: any, wasPlaying: boolean, currentTrack: any,
  ): void {
    const radioIsPlaying = this._isRadioPlaying();
    const url = store.currentStreamUrl || store.alternateStreamUrl;

    store.setActiveConnectorRenderer(null);
    store.setZoneName(zoneDisplayLabel(zone));
    store.setActiveZone(zone);
    viewHandler.setZone(zoneDisplayLabel(zone));
    persistSavedZoneMeta(zone);
    const zoneType = classifySignalPathZone(zone);
    if (zoneType) {
      logSignalPathZoneActivation(zoneType, zoneDisplayLabel(zone));
    }
    try {
      localStorage.setItem('maestro_ios_active_zone', LOCAL_ZONE_STORAGE_ID);
    } catch {}

    if (radioIsPlaying && currentTrack && url) {
      console.log('[ZoneController] iOS: ella-radio → local (gesture-safe)');
      getClientTransport().playLocalStream({ ...currentTrack, streamUrl: url });
    } else if (currentTrack && url) {
      console.log('[ZoneController] iOS: library → local (gesture-safe)');
      getClientTransport().playLocalStream({ ...currentTrack, streamUrl: url });
    }

    if (TransportCommand.isExclusiveActive()) {
      QueueController.suppressAdvance(5000, 'zone_switch');
      TransportCommand.stopExclusiveEngine().catch((err: any) => {
        console.error('[ZoneController] iOS exclusiveStop failed:', err);
      });
    } else if (oldRenderer && wasPlaying) {
      QueueController.suppressAdvance(5000, 'zone_switch');
      TransportCommand.stopRenderer(oldRenderer.id).catch((err: any) => {
        console.error('[ZoneController] iOS stopRenderer failed:', err);
      });
    }
  }

  // ── Tear down: stop old renderer or pause local audio ──
  private async _teardownOldZone(
    isSame: boolean | null, oldRenderer: any, wasPlaying: boolean,
  ): Promise<void> {
    if (isSame) return;

    if (TransportCommand.isExclusiveActive()) {
      QueueController.suppressAdvance(5000, 'zone_switch');
      await TransportCommand.stopExclusiveEngine();
    }

    if (oldRenderer && wasPlaying) {
      QueueController.suppressAdvance(5000, 'zone_switch');
      try {
        await TransportCommand.stopRenderer(oldRenderer.id);
      } catch (err) {
        console.error('[ZoneController] stopRenderer failed, trying pause fallback:', err);
        try {
          await TransportCommand.pauseRenderer(oldRenderer.id);
        } catch (err2) {
          console.error('[ZoneController] pauseRenderer also failed:', err2);
        }
      }
    } else if (!oldRenderer && wasPlaying) {
      const audioEl = document.querySelector('audio') as HTMLAudioElement | null;
      if (audioEl && !audioEl.paused) audioEl.pause();
    }
  }

  // ── Update store to point at the new zone ──
  private _updateStore(zone: any, isLocal: boolean, store: any, viewHandler: any): void {
    if (isLocal) {
      store.setActiveConnectorRenderer(null);
      try {
        const key = isIOSNative() ? 'maestro_ios_active_zone' : 'maestro_connector_active_zone';
        localStorage.setItem(key, LOCAL_ZONE_STORAGE_ID);
      } catch {}
    } else {
      store.setActiveConnectorRenderer(connectorRendererFromZone(zone));
      try {
        const key = isIOSNative() ? 'maestro_ios_active_zone' : 'maestro_connector_active_zone';
        localStorage.setItem(key, zone.id);
      } catch {}
      // Skip volume fetch for fixed-volume renderers (digital pass-through like AirLens)
      if (!zone.capabilities?.fixedVolume) {
        this._fetchRendererVolume(zone.id);
      }
    }
    store.setActiveZone(zone);
    store.setZoneName(zoneDisplayLabel(zone));
    viewHandler.setZone(zoneDisplayLabel(zone));
    const zoneType = classifySignalPathZone(zone);
    if (zoneType) {
      logSignalPathZoneActivation(zoneType, zoneDisplayLabel(zone));
    }
  }

  /** Immediately read the renderer's current volume so the slider is correct on zone switch. */
  private _fetchRendererVolume(rendererId: string): void {
    const p = detectPlatform();
    if (p.isTauriDesktop) {
      import('../lib/connector').then(({ getVolume }) => {
        import('./volumeHydration').then(({ getStartupDisplayVolume, resolveFetchedVolume, writeVolumeCache }) => {
          const store = useMaestroStore.getState();
          if (store.volume == null) {
            store.setVolume(getStartupDisplayVolume(rendererId));
          }
          getVolume(rendererId).then((vol) => {
            const resolved = resolveFetchedVolume(rendererId, vol);
            console.log('[ZoneController] Immediate volume fetch:', rendererId, vol, '→', resolved);
            useMaestroStore.getState().setVolume(resolved);
            writeVolumeCache(rendererId, resolved);
          }).catch((e) => {
            console.debug('[ZoneController] get_volume failed (poll will catch up):', e);
          });
        });
      });
    } else if (p.isIOSNativeApp) {
      const native = (window as any).__maestroNative;
      if (native?.getVolume) native.getVolume(rendererId);
    }
  }

  // ── Sync-aware adoption: if the renderer is already playing via another device ──
  private _trySyncAdopt(zone: any, isSame: boolean | null, store: any): boolean {
    const synced = getSyncHandler().getRemoteSession();
    if (!synced) return false;

    if (zone.id === synced.rendererId && synced.playbackState === 'playing') {
      console.log('[ZoneController] Adopting synced session by ID:', zone.name);
      store.setRendererHasStream(true);
      getSyncHandler().claimControl();
      return true;
    }

    if (synced.rendererName && !isSame
        && zone.name?.toLowerCase() === synced.rendererName.toLowerCase()
        && synced.playbackState === 'playing') {
      console.log('[ZoneController] Adopting synced session by name:', zone.name);
      store.setActiveConnectorRenderer(connectorRendererFromZone(zone));
      store.setRendererHasStream(true);
      getSyncHandler().claimControl();
      return true;
    }

    return false;
  }

  // ── Hand off playback to the new zone ──
  private async _handoffPlayback(
    zone: any, isLocal: boolean, radioIsPlaying: boolean,
    currentTrack: any, store: any,
  ): Promise<void> {
    if (radioIsPlaying) {
      await this._handoffRadio(zone, isLocal);
    } else if (currentTrack) {
      await this._handoffLibrary(zone, isLocal, currentTrack, store);
    }
  }

  private async _handoffRadio(zone: any, isLocal: boolean): Promise<void> {
    const store = useMaestroStore.getState();
    const currentTrack = store.currentTrack;
    if (!currentTrack) return;
    await this._handoffLibrary(zone, isLocal, currentTrack, store);
  }

  private async _handoffLibrary(
    zone: any, isLocal: boolean, currentTrack: any, store: any,
  ): Promise<void> {
    const primaryUrl = store.currentStreamUrl;
    const primaryFmt = store.currentStreamFormatId;
    const altUrl = store.alternateStreamUrl;
    const altFmt = store.alternateStreamFormatId;

    if (isLocal) {
      const url = primaryUrl || altUrl;
      if (url) getClientTransport().playLocalStream({ ...currentTrack, streamUrl: url });
      return;
    }

    // Resolve the best URL + format for this renderer
    const { url, formatId } = await this._resolveUrlForRenderer(
      zone, primaryUrl, primaryFmt, altUrl, altFmt, currentTrack,
    );

    if (url) {
      try {
        const audioEl = document.querySelector('audio') as HTMLAudioElement | null;
        if (audioEl && !audioEl.paused) audioEl.pause();
        await TransportCommand.playUri(url, zone.id, formatId, currentTrack);
      } catch (err) {
        console.error('[ZoneController] playUri failed:', err);
      }
    } else {
      console.error('[ZoneController] NO URL AVAILABLE — stream cache is empty');
    }
  }

  // ── Format negotiation: pick the right cached URL or re-resolve ──
  private async _resolveUrlForRenderer(
    zone: any,
    primaryUrl: string | null, primaryFmt: number | null,
    altUrl: string | null, altFmt: number | null,
    currentTrack: any,
  ): Promise<{ url: string | null; formatId: number }> {
    const renderer = { id: zone.id, name: zoneDisplayLabel(zone), manufacturer: zone.manufacturer || '' };
    const needsCD = isLimitedRenderer(renderer);
    let url: string | null = null;
    let formatId = 27;

    if (needsCD) {
      if (primaryFmt === 6 && primaryUrl) { url = primaryUrl; formatId = 6; }
      else if (altFmt === 6 && altUrl) { url = altUrl; formatId = 6; }
      else if (primaryUrl) { url = primaryUrl; formatId = primaryFmt || 27; }
    } else {
      if (primaryFmt === 27 && primaryUrl) { url = primaryUrl; formatId = 27; }
      else if (altFmt === 27 && altUrl) { url = altUrl; formatId = 27; }
      else if (!url) {
        try {
          if (currentTrack && isLocalLibraryTrack(currentTrack) && currentTrack.file_path) {
            const ws = getClientTransport().getWebSocket();
            const localUrl = await resolveLocalFileUrl(
              currentTrack.file_path,
              ws,
              currentTrack.machine_id,
            );
            if (localUrl) {
              url = localUrl;
              formatId = getFormatIdForRenderer(renderer);
            }
          }
          if (!url) {
            const resolved = await QueueController.resolveStreamUrl(currentTrack, renderer);
            if (resolved) { url = resolved.url; formatId = resolved.formatId || 27; }
          }
        } catch {}
        if (!url && primaryUrl) { url = primaryUrl; formatId = primaryFmt || 27; }
      }
    }

    if (!url && (primaryUrl || altUrl)) {
      url = primaryUrl || altUrl;
      formatId = (primaryUrl ? primaryFmt : altFmt) || 27;
    }

    return { url, formatId };
  }

  private _coldStartLocal(currentTrack: any, store: any): void {
    const url = store.currentStreamUrl || store.alternateStreamUrl;
    if (url) {
      console.log('[ZoneController] Cold start on local zone');
      getClientTransport().playLocalStream({ ...currentTrack, streamUrl: url });
    }
  }

  private _isRadioPlaying(): boolean {
    if (!isRadioActive()) return false;
    const store = useMaestroStore.getState();
    return store.playbackState === 'playing';
  }
}

export const ZoneController = new ZoneControllerImpl();

(window as any).ZoneController = ZoneController;
